import type { ComponentType } from 'react';
import type { RouteObject } from 'react-router-dom';
import {
  EMPLOYEE_PERMISSIONS_VIEW_KEYS,
  PERMISSIONS,
  ROLES_MANAGEMENT_PERMISSIONS,
} from '@shared/constants/permissions';
import type { AppRouteOptions } from '@shared/router/build-routes';
import { PermissionGuard } from '@shared/router/guards/PermissionGuard';
import { lazyPage, type PageGuard, type PageRouteHandle } from '@shared/router/lazy-page';
import { crudRoute } from '@shared/crud/crud-route';
import { LegacyRedirect } from '@/router/legacy-redirect';
import { LEGACY_REDIRECTS, legacyRoutePath } from '@/router/legacy-redirects';
import { PATHS } from '@/router/paths';

/**
 * Finance screens. Every route is loaded on demand and carries the key(s)
 * its backend endpoint requires; the shared tree adds authentication and
 * FINANCE application access above them. The backend re-checks every request.
 *
 * Finance & Accounts screens need the individually granted `finance.access`,
 * plus `finance.view` wherever the screen shows money — the same pair
 * finance-service's `FinanceModuleGuard` and route keys enforce. Create / edit
 * screens re-check `finance.manage` themselves (and the API enforces it).
 */
const FINANCE_ACCESS: PageGuard = { allOf: [PERMISSIONS.finance.access] };
const FINANCE_VIEW: PageGuard = { allOf: [PERMISSIONS.finance.access, PERMISSIONS.finance.view] };
/**
 * Recording a customer receipt: `invoice-payments.create` is the endpoint's
 * key, and the form names the cash or bank account from the chart of
 * accounts, which is read with `finance.access` + `finance.view`.
 */
const RECEIPT_CREATE: PageGuard = {
  allOf: [PERMISSIONS.invoicePayments.create, PERMISSIONS.finance.access, PERMISSIONS.finance.view],
};

const page = (path: string, load: () => Promise<ComponentType>, guard?: PageGuard) =>
  lazyPage(path, load, guard);

/**
 * Moved Vendor Management addresses (old "Expenses" and "Payments" screens,
 * reminder-email links) → their current screens. Every target is a money
 * screen, so the redirect sits behind the same keys as the screen it opens.
 */
const legacyRoutes: RouteObject[] = LEGACY_REDIRECTS.map((rule) => ({
  path: legacyRoutePath(rule),
  handle: { guard: FINANCE_VIEW } satisfies PageRouteHandle,
  element: (
    <PermissionGuard {...FINANCE_VIEW}>
      <LegacyRedirect rule={rule} />
    </PermissionGuard>
  ),
}));

const shellRoutes: RouteObject[] = [
  ...legacyRoutes,

  // --------------------------------------------------- Finance & Accounts
  // Vendor Management: Vendor Master (vendors) and Services & Subscriptions.
  // Accounts Payable: Supplier Bills (expenses) and the Payment Register
  // (payments) keep their /finance/vendor-management addresses; the rest of
  // the section is under /finance/payables.
  page(
    PATHS.app.financeOverview,
    async () => (await import('@/modules/finance/overview/overview-page')).FinanceOverviewPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.financeVendors,
    async () => (await import('@/modules/finance/vendors/vendors-page')).VendorsPage,
    FINANCE_ACCESS,
  ),
  page(
    PATHS.app.financeVendorNew,
    async () => (await import('@/modules/finance/vendors/vendor-form-page')).VendorFormPage,
    FINANCE_ACCESS,
  ),
  page(
    PATHS.app.financeVendorDetail(),
    async () => (await import('@/modules/finance/vendors/vendor-detail-page')).VendorDetailPage,
    FINANCE_ACCESS,
  ),
  page(
    PATHS.app.financeVendorEdit(),
    async () => (await import('@/modules/finance/vendors/vendor-form-page')).VendorFormPage,
    FINANCE_ACCESS,
  ),
  page(
    PATHS.app.financeServices,
    async () => (await import('@/modules/finance/services/services-page')).ServicesPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.financeServiceNew,
    async () => (await import('@/modules/finance/services/service-form-page')).ServiceFormPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.financeServiceDetail(),
    async () => (await import('@/modules/finance/services/service-detail-page')).ServiceDetailPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.financeServiceEdit(),
    async () => (await import('@/modules/finance/services/service-form-page')).ServiceFormPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.financeServiceBill(),
    async () => (await import('@/modules/finance/services/record-bill-page')).RecordBillPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.financeExpenses,
    async () => (await import('@/modules/finance/expenses/expenses-page')).ExpensesPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.financeExpenseNew,
    async () => (await import('@/modules/finance/expenses/expense-form-page')).ExpenseFormPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.financeExpenseDetail(),
    async () => (await import('@/modules/finance/expenses/expense-detail-page')).ExpenseDetailPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.financeExpenseEdit(),
    async () => (await import('@/modules/finance/expenses/expense-form-page')).ExpenseFormPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.financeExpensePay(),
    async () => (await import('@/modules/finance/expenses/record-payment-page')).RecordPaymentPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.financePaymentDetail(),
    async () => (await import('@/modules/finance/payments/payment-detail-page')).PaymentDetailPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.financePayments,
    async () => (await import('@/modules/finance/payments/payments-page')).PaymentsPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.financeReimbursements,
    async () =>
      (await import('@/modules/finance/reimbursements/reimbursements-page')).ReimbursementsPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.financeReimbursementDetail(),
    async () =>
      (await import('@/modules/finance/reimbursements/reimbursement-page')).ReimbursementPage,
    FINANCE_VIEW,
  ),

  page(
    PATHS.app.financeDashboard,
    async () =>
      (await import('@/modules/finance/accounting/dashboard-page')).AccountingDashboardPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.financeServiceForecast,
    async () => (await import('@/modules/finance/services/forecast-page')).RenewalForecastPage,
    FINANCE_VIEW,
  ),

  // Accounts Payable: vendor payments (with refunds), vendor credits, the
  // vendor ledger and the aging.
  page(
    PATHS.app.financeVendorPayments,
    async () =>
      (await import('@/modules/finance/payables/vendor-payments-page')).VendorPaymentsPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.financeVendorPaymentNew,
    async () =>
      (await import('@/modules/finance/payables/vendor-payment-form-page')).VendorPaymentFormPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.financeVendorPaymentDetail(),
    async () =>
      (await import('@/modules/finance/payables/vendor-payment-detail-page'))
        .VendorPaymentDetailPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.financeVendorRefunds,
    async () => (await import('@/modules/finance/payables/vendor-refunds-page')).VendorRefundsPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.financeVendorCredits,
    async () => (await import('@/modules/finance/payables/vendor-credits-page')).VendorCreditsPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.financeVendorCreditNew,
    async () =>
      (await import('@/modules/finance/payables/vendor-credit-form-page')).VendorCreditFormPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.financeVendorCreditDetail(),
    async () =>
      (await import('@/modules/finance/payables/vendor-credit-detail-page')).VendorCreditDetailPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.financeVendorCreditEdit(),
    async () =>
      (await import('@/modules/finance/payables/vendor-credit-form-page')).VendorCreditFormPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.financeVendorLedger,
    async () => (await import('@/modules/finance/payables/vendor-ledger-page')).VendorLedgerPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.financeApAging,
    async () => (await import('@/modules/finance/accounting/aging-page')).ApAgingPage,
    FINANCE_VIEW,
  ),

  // ------------------------------------------------------------- Accounting
  // Reading needs finance.access + finance.view; each screen re-checks the
  // manage / approve key of its actions (and the API enforces them).
  page(
    PATHS.app.accountingSetup,
    async () => (await import('@/modules/finance/accounting/setup-page')).AccountingSetupPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.accountingAccounts,
    async () => (await import('@/modules/finance/accounting/accounts-page')).ChartOfAccountsPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.accountingJournal,
    async () => (await import('@/modules/finance/accounting/journal-page')).JournalPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.accountingJournalNew,
    async () => (await import('@/modules/finance/accounting/journal-form-page')).JournalFormPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.accountingJournalEntry(),
    async () => (await import('@/modules/finance/accounting/journal-entry-page')).JournalEntryPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.accountingJournalEdit(),
    async () => (await import('@/modules/finance/accounting/journal-form-page')).JournalFormPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.accountingGeneralLedger,
    async () =>
      (await import('@/modules/finance/accounting/general-ledger-page')).GeneralLedgerPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.accountingPeriods,
    async () => (await import('@/modules/finance/accounting/periods-page')).PeriodsPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.accountingOpeningBalances,
    async () =>
      (await import('@/modules/finance/accounting/opening-balances-page')).OpeningBalancesPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.accountingOpeningNew,
    async () => (await import('@/modules/finance/accounting/opening-batch-page')).OpeningBatchPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.accountingOpeningReconciliation,
    async () =>
      (await import('@/modules/finance/accounting/opening-reconciliation-page'))
        .OpeningReconciliationPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.accountingOpeningBatch(),
    async () => (await import('@/modules/finance/accounting/opening-batch-page')).OpeningBatchPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.accountingProfitLoss,
    async () => (await import('@/modules/finance/accounting/reports/profit-loss-page')).ProfitLossPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.accountingBalanceSheet,
    async () =>
      (await import('@/modules/finance/accounting/reports/balance-sheet-page')).BalanceSheetPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.accountingCashFlow,
    async () => (await import('@/modules/finance/accounting/reports/cash-flow-page')).CashFlowPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.accountingTrialBalance,
    async () =>
      (await import('@/modules/finance/accounting/reports/trial-balance-page')).TrialBalancePage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.accountingTaxSummary,
    async () => (await import('@/modules/finance/accounting/reports/tax-summary-page')).TaxSummaryPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.accountingControlReconciliation,
    async () =>
      (await import('@/modules/finance/accounting/reports/control-reconciliation-page'))
        .ControlReconciliationPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.accountingImports,
    async () => (await import('@/modules/finance/accounting/imports-page')).ImportsPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.accountingImportNew,
    async () => (await import('@/modules/finance/accounting/import-new-page')).ImportNewPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.accountingImport(),
    async () => (await import('@/modules/finance/accounting/import-detail-page')).ImportDetailPage,
    FINANCE_VIEW,
  ),

  // --------------------------------------------------------------- Treasury
  page(
    PATHS.app.treasuryCashPosition,
    async () => (await import('@/modules/finance/treasury/cash-position-page')).CashPositionPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.treasuryReconciliation,
    async () =>
      (await import('@/modules/finance/treasury/reconciliation-page')).BankReconciliationPage,
    FINANCE_VIEW,
  ),
  // Bank statements: import (manage) and match lines to the ledger.
  page(
    PATHS.app.treasuryStatements,
    async () => (await import('@/modules/finance/treasury/statements-page')).BankStatementsPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.treasuryStatementImport,
    async () =>
      (await import('@/modules/finance/treasury/statement-import-page')).BankStatementImportPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.treasuryStatement(),
    async () => (await import('@/modules/finance/treasury/statement-detail-page')).BankStatementPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.treasuryTransfers,
    async () => (await import('@/modules/finance/treasury/transfers-page')).TransfersPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.treasuryTransferNew,
    async () => (await import('@/modules/finance/treasury/transfer-form-page')).TransferFormPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.treasuryTransfer(),
    async () => (await import('@/modules/finance/treasury/transfer-detail-page')).TransferDetailPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.treasuryTransferEdit(),
    async () => (await import('@/modules/finance/treasury/transfer-form-page')).TransferFormPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.treasuryLoans,
    async () => (await import('@/modules/finance/treasury/loans-page')).LoansPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.treasuryLoanNew,
    async () => (await import('@/modules/finance/treasury/loan-form-page')).LoanFormPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.treasuryLoan(),
    async () => (await import('@/modules/finance/treasury/loan-detail-page')).LoanDetailPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.treasuryLoanEdit(),
    async () => (await import('@/modules/finance/treasury/loan-form-page')).LoanFormPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.treasuryInvestments,
    async () => (await import('@/modules/finance/treasury/investments-page')).InvestmentsPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.treasuryInvestmentNew,
    async () =>
      (await import('@/modules/finance/treasury/investment-form-page')).InvestmentFormPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.treasuryInvestment(),
    async () =>
      (await import('@/modules/finance/treasury/investment-detail-page')).InvestmentDetailPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.treasuryInvestmentEdit(),
    async () =>
      (await import('@/modules/finance/treasury/investment-form-page')).InvestmentFormPage,
    FINANCE_VIEW,
  ),

  // Asset Management — `finance.access` opens it; cost and value appear only
  // with `finance.view` (masked by the API); changes need `finance.manage`.
  page(
    PATHS.app.financeAssetOverview,
    async () => (await import('@/modules/finance/assets/asset-overview-page')).AssetOverviewPage,
    FINANCE_ACCESS,
  ),
  page(
    PATHS.app.financeAssets,
    async () => (await import('@/modules/finance/assets/assets-page')).AssetsPage,
    FINANCE_ACCESS,
  ),
  page(
    PATHS.app.financeAssetNew,
    async () => (await import('@/modules/finance/assets/asset-form-page')).AssetFormPage,
    FINANCE_ACCESS,
  ),
  page(
    PATHS.app.financeAssetDetail(),
    async () => (await import('@/modules/finance/assets/asset-detail-page')).AssetDetailPage,
    FINANCE_ACCESS,
  ),
  page(
    PATHS.app.financeAssetEdit(),
    async () => (await import('@/modules/finance/assets/asset-form-page')).AssetFormPage,
    FINANCE_ACCESS,
  ),
  page(
    PATHS.app.financeAssetAssign(),
    async () => (await import('@/modules/finance/assets/assign-asset-page')).AssignAssetPage,
    FINANCE_ACCESS,
  ),
  page(
    PATHS.app.financeAssetTransfer(),
    async () => (await import('@/modules/finance/assets/transfer-asset-page')).TransferAssetPage,
    FINANCE_ACCESS,
  ),
  page(
    PATHS.app.financeAssetReturn(),
    async () => (await import('@/modules/finance/assets/return-asset-page')).ReturnAssetPage,
    FINANCE_ACCESS,
  ),
  page(
    PATHS.app.financeAssetDispose(),
    async () => (await import('@/modules/finance/assets/dispose-asset-page')).DisposeAssetPage,
    FINANCE_ACCESS,
  ),
  page(
    PATHS.app.financeAssetAssignments,
    async () => (await import('@/modules/finance/assets/assignments-page')).AssignmentsPage,
    FINANCE_ACCESS,
  ),
  page(
    PATHS.app.financeAssetRepairs,
    async () => (await import('@/modules/finance/assets/repairs-page')).RepairsPage,
    FINANCE_ACCESS,
  ),
  page(
    PATHS.app.financeAssetRepairNew,
    async () => (await import('@/modules/finance/assets/repair-form-page')).RepairFormPage,
    FINANCE_ACCESS,
  ),
  page(
    PATHS.app.financeAssetRepairDetail(),
    async () => (await import('@/modules/finance/assets/repair-detail-page')).RepairDetailPage,
    FINANCE_ACCESS,
  ),
  page(
    PATHS.app.financeAssetRepairEdit(),
    async () => (await import('@/modules/finance/assets/repair-form-page')).RepairFormPage,
    FINANCE_ACCESS,
  ),
  page(
    PATHS.app.financeAssetRepairComplete(),
    async () => (await import('@/modules/finance/assets/complete-repair-page')).CompleteRepairPage,
    FINANCE_ACCESS,
  ),
  page(
    PATHS.app.financeAssetDisposals,
    async () => (await import('@/modules/finance/assets/disposals-page')).DisposalsPage,
    FINANCE_ACCESS,
  ),

  // ---------------------------------------------------------------- clients
  page(PATHS.app.clients, async () => (await import('@/modules/clients/page')).ClientsPage, {
    permission: PERMISSIONS.clients.view,
  }),
  page(
    PATHS.app.clientNew,
    async () => (await import('@/modules/clients/form-page')).ClientCreatePage,
    { permission: PERMISSIONS.clients.manage },
  ),
  page(
    PATHS.app.clientEdit(),
    async () => (await import('@/modules/clients/form-page')).ClientEditPage,
    { permission: PERMISSIONS.clients.manage },
  ),
  page(
    PATHS.app.clientDetail(),
    async () => (await import('@/modules/clients/detail-page')).ClientDetailPage,
    { permission: PERMISSIONS.clients.view },
  ),

  // -------------------------------------------------------------- invoicing
  // Each guard is the key the backend requires for that surface: the tax,
  // item, template and numbering catalogues have their own view/manage keys.
  page(
    PATHS.app.invoices,
    async () => (await import('@/modules/invoicing/invoices/page')).InvoicesPage,
    { permission: PERMISSIONS.invoices.view },
  ),
  page(
    PATHS.app.invoiceCreate,
    async () => (await import('@/modules/invoicing/form/form-page')).InvoiceCreatePage,
    { permission: PERMISSIONS.invoices.create },
  ),
  page(
    PATHS.app.invoiceEdit(),
    async () => (await import('@/modules/invoicing/form/form-page')).InvoiceEditPage,
    { permission: PERMISSIONS.invoices.update },
  ),
  page(
    PATHS.app.invoiceDetail(),
    async () => (await import('@/modules/invoicing/detail/detail-page')).InvoiceDetailPage,
    { permission: PERMISSIONS.invoices.view },
  ),
  page(
    PATHS.app.invoiceTemplates,
    async () => (await import('@/modules/invoicing/templates/page')).InvoiceTemplatesPage,
    { permission: PERMISSIONS.invoiceTemplates.view },
  ),
  page(
    PATHS.app.invoiceNumbering,
    async () => (await import('@/modules/invoicing/numbering/page')).BillNumberingPage,
    { permission: PERMISSIONS.invoiceNumbering.view },
  ),
  page(
    PATHS.app.invoiceItems,
    async () => (await import('@/modules/invoicing/items/page')).InvoiceItemsPage,
    { permission: PERMISSIONS.invoiceItems.view },
  ),
  page(
    PATHS.app.invoiceItemCreate,
    async () => (await import('@/modules/invoicing/items/item-form-page')).InvoiceItemCreatePage,
    { permission: PERMISSIONS.invoiceItems.manage },
  ),
  page(
    PATHS.app.invoiceItemEdit(),
    async () => (await import('@/modules/invoicing/items/item-form-page')).InvoiceItemEditPage,
    { permission: PERMISSIONS.invoiceItems.manage },
  ),
  page(
    PATHS.app.invoiceTaxes,
    async () => (await import('@/modules/invoicing/taxes/page')).InvoiceTaxesPage,
    { permission: PERMISSIONS.invoiceTaxes.view },
  ),
  page(
    PATHS.app.invoiceTaxCreate,
    async () => (await import('@/modules/invoicing/taxes/tax-form-page')).InvoiceTaxCreatePage,
    { permission: PERMISSIONS.invoiceTaxes.manage },
  ),
  page(
    PATHS.app.invoiceTaxEdit(),
    async () => (await import('@/modules/invoicing/taxes/tax-form-page')).InvoiceTaxEditPage,
    { permission: PERMISSIONS.invoiceTaxes.manage },
  ),
  page(
    PATHS.app.invoiceTaxView(),
    async () => (await import('@/modules/invoicing/taxes/tax-form-page')).InvoiceTaxViewPage,
    { permission: PERMISSIONS.invoiceTaxes.view },
  ),

  // ------------------------------------------------------------ receivables
  // Receipts and refunds: invoice-payments.*; credit notes: invoices.*; the
  // aging and client statements read the ledger (finance.access + view).
  page(
    PATHS.app.receipts,
    async () => (await import('@/modules/invoicing/receivables/receipts-page')).ReceiptsPage,
    { permission: PERMISSIONS.invoicePayments.view },
  ),
  page(
    PATHS.app.receiptNew,
    async () => (await import('@/modules/invoicing/receivables/receipt-form-page')).ReceiptFormPage,
    RECEIPT_CREATE,
  ),
  page(
    PATHS.app.receipt(),
    async () =>
      (await import('@/modules/invoicing/receivables/receipt-detail-page')).ReceiptDetailPage,
    { permission: PERMISSIONS.invoicePayments.view },
  ),
  page(
    PATHS.app.customerRefunds,
    async () =>
      (await import('@/modules/invoicing/receivables/refunds-page')).CustomerRefundsPage,
    { permission: PERMISSIONS.invoicePayments.view },
  ),
  page(
    PATHS.app.creditNotes,
    async () =>
      (await import('@/modules/invoicing/receivables/credit-notes-page')).CreditNotesPage,
    { permission: PERMISSIONS.invoices.view },
  ),
  page(
    PATHS.app.creditNoteNew,
    async () =>
      (await import('@/modules/invoicing/receivables/credit-note-form-page')).CreditNoteFormPage,
    { permission: PERMISSIONS.invoices.create },
  ),
  page(
    PATHS.app.creditNote(),
    async () =>
      (await import('@/modules/invoicing/receivables/credit-note-detail-page'))
        .CreditNoteDetailPage,
    { permission: PERMISSIONS.invoices.view },
  ),
  page(
    PATHS.app.creditNoteEdit(),
    async () =>
      (await import('@/modules/invoicing/receivables/credit-note-form-page')).CreditNoteFormPage,
    { permission: PERMISSIONS.invoices.update },
  ),
  page(
    PATHS.app.arAging,
    async () => (await import('@/modules/finance/accounting/aging-page')).ArAgingPage,
    FINANCE_VIEW,
  ),
  page(
    PATHS.app.customerLedger,
    async () =>
      (await import('@/modules/finance/accounting/customer-ledger-page')).CustomerLedgerPage,
    FINANCE_VIEW,
  ),

  // ----------------------------------------------- company master data
  page(PATHS.app.companies, async () => (await import('@/modules/companies/page')).CompaniesPage, {
    permission: PERMISSIONS.companies.view,
  }),
  page(
    PATHS.app.companyNew,
    async () => (await import('@/modules/companies/wizard/create-page')).CompanyCreatePage,
    { permission: PERMISSIONS.companies.manage },
  ),
  page(
    PATHS.app.companyEdit(),
    async () => (await import('@/modules/companies/edit-page')).CompanyEditPage,
    { permission: PERMISSIONS.companies.manage },
  ),
  page(
    PATHS.app.companyDetail(),
    async () => (await import('@/modules/companies/detail-page')).CompanyDetailPage,
    { permission: PERMISSIONS.companies.view },
  ),
  // Employees — People / HRMS data through those services' own APIs; the
  // screens also need the HRMS application, which they check themselves.
  page(
    PATHS.app.employees,
    async () => (await import('@/modules/employees/employees-page')).EmployeesPage,
    { permission: PERMISSIONS.employees.view },
  ),
  page(
    PATHS.app.employeeDetail(),
    async () => (await import('@/modules/employees/employee-detail-page')).EmployeeDetailPage,
    { permission: PERMISSIONS.employees.view },
  ),
  page(
    PATHS.app.employeeEdit(),
    async () => (await import('@/modules/employees/employee-edit-page')).EmployeeEditPage,
    { allOf: [PERMISSIONS.employees.view, PERMISSIONS.employees.update] },
  ),
  crudRoute(
    PATHS.app.vendorCategories,
    'vendor-categories',
    async () =>
      (await import('@/modules/categories/vendor-categories/config')).vendorCategoryConfig,
  ),
  crudRoute(
    PATHS.app.assetCategories,
    'asset-categories',
    async () => (await import('@/modules/categories/asset-categories/config')).assetCategoryConfig,
  ),

  // -------------------------------------------------- access administration
  page(
    PATHS.app.accessUsers,
    async () => (await import('@/modules/access/users-page')).AccessUsersPage,
    { permission: PERMISSIONS.users.view },
  ),
  page(
    PATHS.app.accessUserNew,
    async () => (await import('@/modules/access/user-new-page')).AccessUserNewPage,
    { permission: PERMISSIONS.users.manage },
  ),
  page(
    PATHS.app.accessUserDetail(),
    async () => (await import('@/modules/access/user-detail-page')).AccessUserDetailPage,
    { permission: PERMISSIONS.users.view },
  ),
  page(
    PATHS.app.employeePermissions(),
    async () =>
      (await import('@/modules/access/employee-permissions-route')).AccessEmployeePermissionsPage,
    { anyOf: EMPLOYEE_PERMISSIONS_VIEW_KEYS },
  ),
  // `roles.view` is held by everyone for role pickers; the screen is for those who manage roles.
  page(PATHS.app.roles, async () => (await import('@shared/domain/access/roles-page')).RolesPage, {
    anyOf: ROLES_MANAGEMENT_PERMISSIONS,
  }),
  page(
    PATHS.app.permissions,
    async () => (await import('@shared/domain/access/permissions-page')).PermissionsPage,
    { permission: PERMISSIONS.permissions.view },
  ),

  // ------------------------------------------------------------------ audit
  page(
    PATHS.app.auditLogs,
    async () => (await import('@shared/domain/audit/audit-logs-page')).AuditLogsPage,
    { permission: PERMISSIONS.auditLogs.view },
  ),
];

/** The unsaved-invoice preview opens in its own tab, without the shell. */
const bareRoutes: RouteObject[] = [
  page(
    PATHS.app.invoicePreview(),
    async () =>
      (await import('@/modules/invoicing/detail/invoice-preview-page')).InvoicePreviewPage,
    {
      permission: PERMISSIONS.invoices.view,
    },
  ),
];

export const financeRoutes: AppRouteOptions = { shellRoutes, bareRoutes };
